<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { getAnchorOffset } from '~/composables/useAnchorScroll'

const props = withDefaults(defineProps<{
  label: string
  align?: 'start' | 'end'
  placement?: 'bottom' | 'right'
  hover?: boolean
  avoidHeader?: boolean
}>(), { align: 'end', placement: 'bottom', hover: false, avoidHeader: true })

const open = defineModel<boolean>('open', { default: false })
const root = ref<HTMLElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const menuID = useId()
const popupStyle = ref<CSSProperties>({})
let closeTimer: ReturnType<typeof setTimeout> | null = null

// clearCloseTimer 取消跨越触发按钮与菜单间隙时的延迟关闭
const clearCloseTimer = () => {
  if (closeTimer) clearTimeout(closeTimer)
  closeTimer = null
}

// updatePosition 将菜单定位到按钮下方或侧边，并限制在可见区域内
const updatePosition = async () => {
  if (!open.value || !root.value || !menu.value) return
  const rect = root.value.getBoundingClientRect()
  const edge = 12
  const side = props.placement === 'right'
  const gap = side ? 12 : 8
  const availableWidth = window.innerWidth - edge * 2
  popupStyle.value.width = side ? `${Math.min(180, availableWidth)}px` : 'max-content'
  popupStyle.value.minWidth = `${Math.min(side ? 180 : Math.max(rect.width, 160), availableWidth)}px`
  await nextTick()
  if (!open.value || !menu.value) return

  const topBoundary = side ? 16 : props.avoidHeader ? getAnchorOffset() : edge
  const bottomBoundary = window.innerHeight - (side ? 16 : edge)
  const belowTop = Math.max(rect.bottom + gap, topBoundary)
  const aboveBottom = Math.min(rect.top - gap, bottomBoundary)
  const below = Math.max(0, bottomBoundary - belowTop)
  const above = Math.max(0, aboveBottom - topBoundary)
  const upward = !side && menu.value.scrollHeight > below && above > below
  const height = side ? bottomBoundary - topBoundary : upward ? above : below
  const width = Math.min(menu.value.offsetWidth, availableWidth)
  const left = side ? rect.right + gap : props.align === 'end' ? rect.right - width : rect.left
  const top = side
    ? Math.max(topBoundary, Math.min(rect.top, bottomBoundary - Math.min(menu.value.scrollHeight, height)))
    : upward ? aboveBottom - Math.min(menu.value.scrollHeight, height) : belowTop
  popupStyle.value = {
    ...popupStyle.value,
    left: `${Math.max(edge, Math.min(left, window.innerWidth - width - edge))}px`,
    top: `${top}px`,
    maxHeight: `${height}px`,
    transformOrigin: side ? 'top left' : `${upward ? 'bottom' : 'top'} ${props.align === 'end' ? 'right' : 'left'}`
  }
}

// getMenuItems 获取可操作的选项和导航链接
const getMenuItems = () => Array.from<HTMLElement>(
  menu.value?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]') || []
)

// openMenu 展开菜单，键盘和点击打开时聚焦已选项或首项
const openMenu = async (focusMenu = true) => {
  clearCloseTimer()
  if (!root.value) return
  popupStyle.value = {
    ...popupStyle.value,
    zIndex: getComputedStyle(root.value).getPropertyValue('--dropdown-z-index').trim() || (props.avoidHeader ? '20' : '40')
  }
  open.value = true
  await nextTick()
  await updatePosition()
  if (!open.value || !focusMenu) return
  const items = getMenuItems()
  const selected = items.find(item => item.getAttribute('aria-checked') === 'true' || item.getAttribute('aria-current') === 'page')
  const target = selected || items[0]
  target?.focus()
}

// closeMenu 收起菜单并将焦点返回触发按钮
const closeMenu = () => {
  clearCloseTimer()
  open.value = false
  root.value?.querySelector('button')?.focus()
}

// toggleMenu 切换触发按钮对应的菜单
const toggleMenu = () => {
  if (open.value) closeMenu()
  else void openMenu()
}

// contains 判断焦点或指针是否仍位于触发按钮和菜单内
const contains = (target: Node | null) => root.value?.contains(target) || menu.value?.contains(target)

// handleMouseEnter 在支持悬停的设备上打开导航菜单
const handleMouseEnter = () => {
  clearCloseTimer()
  if (props.hover && !open.value && window.matchMedia('(hover: hover) and (pointer: fine)').matches) void openMenu(false)
}

// handleMouseLeave 留出跨越菜单间隙的时间
const handleMouseLeave = () => {
  if (props.hover) closeTimer = setTimeout(() => { open.value = false }, 100)
}

// handleFocusIn 从外部聚焦顶部导航时展开菜单
const handleFocusIn = (event: FocusEvent) => {
  if (props.hover && !contains(event.relatedTarget as Node | null)
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches) void openMenu(false)
}

// handleFocusOut 焦点明确离开菜单时关闭，触屏空焦点交由外部点击处理
const handleFocusOut = (event: FocusEvent) => {
  const target = event.relatedTarget as Node | null
  if (target && !contains(target)) open.value = false
}

// handleOutsidePointer 点击触发按钮和菜单之外时关闭
const handleOutsidePointer = (event: PointerEvent) => {
  if (!contains(event.target as Node)) open.value = false
}

// handleMenuKeydown 使用方向键和首尾键切换选项或链接
const handleMenuKeydown = (event: KeyboardEvent) => {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  const items = getMenuItems()
  if (!items.length) return
  const index = items.indexOf(event.target as HTMLElement)
  let target = 0
  if (event.key === 'End') target = items.length - 1
  else if (event.key !== 'Home') target = (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
  items[target]?.focus()
}

// handleScroll 仅在菜单外部滚动时同步位置
const handleScroll = (event: Event) => {
  if (!menu.value?.contains(event.target as Node)) void updatePosition()
}

onMounted(() => {
  document.addEventListener('pointerdown', handleOutsidePointer)
  document.addEventListener('scroll', handleScroll, true)
  window.addEventListener('resize', updatePosition)
})
onBeforeUnmount(() => {
  clearCloseTimer()
  document.removeEventListener('pointerdown', handleOutsidePointer)
  document.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', updatePosition)
})
</script>

<template>
  <div
    ref="root"
    class="popup-menu"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @keydown.down.prevent="openMenu()"
    @keydown.esc.stop.prevent="closeMenu"
  >
    <slot name="trigger" :open="open" :toggle="toggleMenu" :menuId="menuID" />
    <Teleport to="body">
      <Transition name="dropdown-fade">
        <div
          v-if="open"
          :id="menuID"
          ref="menu"
          class="dropdown-menu popup-menu-panel"
          :style="popupStyle"
          role="menu"
          :aria-label="label"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
          @keydown="handleMenuKeydown"
          @keydown.esc.stop.prevent="closeMenu"
          @focusout="handleFocusOut"
        >
          <slot :close="closeMenu" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.popup-menu {
  min-width: 0;
  max-width: 100%;
}

.popup-menu-panel {
  position: fixed;
  width: max-content;
  max-width: calc(100vw - 24px);
}
</style>
